import { useMutation, useQuery } from "@apollo/client";
import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import { CardSkeleton } from "@/components/Skeleton";
import { TableHeading } from "@/components/Table";
import { DEPARTMENTS_QUERY } from "@/features/global/components/AddTaskSideSheet/queries";
import { useFlags, useFlagsLoaded } from "@/hooks/useFeatureFlags";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { useAssumeUser } from "@/providers/AssumeUserProvider";
import {
  canShowTaskNotificationPreferences,
  TEAM_TASK_NOTIFICATIONS_FLAG
} from "../../_canShowTaskNotificationPreferences";
import { useActorTaskNotificationAccess } from "../../_useActorTaskNotificationAccess";
import DestinationEditor, { type DestinationDraft } from "./_components/DestinationEditor";
import { isPgaSectionWorkdayDepartment } from "./_isPgaSectionWorkdayDepartment";
import {
  UPSERT_TASK_DEPARTMENT_NOTIFICATION_DESTINATION_MUTATION,
  type UpsertTaskDepartmentNotificationDestinationInput
} from "./mutations";
import { TASK_DEPARTMENT_NOTIFICATION_DESTINATIONS_QUERY } from "./queries";

const PREFERENCES_COLUMN_CLASS = "mx-auto max-w-3xl pb-12";

function blankDraft(workdayDepartmentName: string): DestinationDraft {
  return {
    workdayDepartmentName,
    email: null,
    emailEnabled: false,
    emailLastError: null
  };
}

export default function TaskNotificationPreferencesPage() {
  const flags = useFlags();
  const flagsLoaded = useFlagsLoaded();
  const { loaded: accessLoaded, isGlobalAdmin, canManage, actorDepartment } = useActorTaskNotificationAccess();
  const { assumedUser } = useAssumeUser();
  const assumingUser = Boolean(assumedUser);
  const flagOn = flags[TEAM_TASK_NOTIFICATIONS_FLAG] === true;
  const canShow = canShowTaskNotificationPreferences({
    flagOn,
    flagsLoaded,
    accessLoaded,
    isGlobalAdmin,
    canManage,
    actorDepartment
  });

  const { data, loading, error } = useQuery(TASK_DEPARTMENT_NOTIFICATION_DESTINATIONS_QUERY, {
    skip: !canShow
  });
  const {
    data: departmentsData,
    loading: departmentsLoading,
    error: departmentsError
  } = useQuery(DEPARTMENTS_QUERY, {
    skip: !canShow || !isGlobalAdmin || assumingUser
  });
  const [upsertDestination] = useMutation(UPSERT_TASK_DEPARTMENT_NOTIFICATION_DESTINATION_MUTATION);
  const [savingDepartment, setSavingDepartment] = useState<string | null>(null);

  const destinations = data?.taskDepartmentNotificationDestinations;
  const drafts = useMemo<DestinationDraft[]>(() => {
    const saved = destinations ?? [];
    const byName = new Map(saved.map((destination) => [destination.workdayDepartmentName, destination]));
    const names = isGlobalAdmin
      ? [
          ...new Set([
            ...(departmentsData?.departments ?? []),
            ...saved.map((destination) => destination.workdayDepartmentName)
          ])
        ]
      : actorDepartment
        ? [actorDepartment]
        : [];

    names.sort((left, right) => left.localeCompare(right));
    const draftsForNames = names.map((name) => byName.get(name) ?? blankDraft(name));
    if (!isGlobalAdmin) {
      return draftsForNames;
    }

    return draftsForNames.filter((destination) => !isPgaSectionWorkdayDepartment(destination.workdayDepartmentName));
  }, [actorDepartment, departmentsData?.departments, destinations, isGlobalAdmin]);

  if (!flagsLoaded || (flagOn && !accessLoaded)) {
    return (
      <div className={PREFERENCES_COLUMN_CLASS} data-testid="task-notification-preferences">
        <CardSkeleton className="mt-12 w-full" rows={6} />
      </div>
    );
  }

  if (!flagOn || !canShow) {
    return <Navigate to="/tasks/my-tasks" replace />;
  }

  async function saveDestination(input: UpsertTaskDepartmentNotificationDestinationInput) {
    setSavingDepartment(input.workdayDepartmentName);
    try {
      const result = await upsertDestination({
        variables: { input },
        refetchQueries: [TASK_DEPARTMENT_NOTIFICATION_DESTINATIONS_QUERY],
        awaitRefetchQueries: true
      });
      const payload = result.data?.upsertTaskDepartmentNotificationDestination;
      if (!payload?.success) {
        showErrorSnackbar(payload?.message || "Inbox not saved", { subtitle: "Try again." });
        return false;
      }

      showSnackbar("Preference saved");
      return true;
    } catch {
      showErrorSnackbar("Inbox not saved", { subtitle: "Try again." });
      return false;
    } finally {
      setSavingDepartment(null);
    }
  }

  if ((loading && !data) || (isGlobalAdmin && departmentsLoading && !departmentsData && !departmentsError)) {
    return (
      <div className={PREFERENCES_COLUMN_CLASS} data-testid="task-notification-preferences">
        <CardSkeleton className="mt-12 w-full" rows={6} />
      </div>
    );
  }

  if (error || (isGlobalAdmin && departmentsError)) {
    return (
      <div className={PREFERENCES_COLUMN_CLASS} data-testid="task-notification-preferences">
        <ErrorBoundary
          variant="inline"
          title="Team inbox not loaded"
          message="We could not load team inboxes. Try again, or contact tech support."
          className="mt-12"
        />
      </div>
    );
  }

  return (
    <div className={PREFERENCES_COLUMN_CLASS} data-testid="task-notification-preferences">
      <TableHeading
        heading="Team Notification Preferences"
        body="Set notification preferences for when your team is tagged on an unassigned task."
      >
        {drafts.length === 0 ? (
          <EmptyState
            title="No departments found"
            caption="Department cards show here when Workday departments exist."
          />
        ) : (
          <DestinationEditors drafts={drafts} savingDepartment={savingDepartment} onSave={saveDestination} />
        )}
      </TableHeading>
    </div>
  );
}

function DestinationEditors({
  drafts,
  savingDepartment,
  onSave
}: {
  drafts: DestinationDraft[];
  savingDepartment: string | null;
  onSave: (input: UpsertTaskDepartmentNotificationDestinationInput) => Promise<boolean>;
}) {
  return (
    <div className="flex flex-col gap-6">
      {drafts.map((destination) => (
        <DestinationEditor
          key={destination.id ?? destination.workdayDepartmentName}
          destination={destination}
          saving={savingDepartment === destination.workdayDepartmentName}
          onSave={onSave}
        />
      ))}
    </div>
  );
}
